// npm packages
import React, { useEffect, useState, useContext } from "react";
import { toast } from "react-toastify";

// custom pages
import "./AddEmployee";
import Context from "context/context";
import Loader from "components/Loader";
import FileHandler from "apis/panel/fileHandler/fileHandler";
import ReusableFunction from "utils/reusablefunction";
import EmployeeAPI from "apis/panel/employee/employee";

// custom images
import myDocumentsImg from "assets/images/my-documents.png";

// material ui
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { InputAdornment, IconButton } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";

const MyDocuments = () => {
  const employeeId = sessionStorage.getItem("EmployeeID");
  const { dispatch } = useContext(Context);

  const { putMyDocumentsAPI, getEmployeeCodeAPI } = EmployeeAPI();
  const { fileUploadAPI } = FileHandler();
  const { handleFileInputReselect } = ReusableFunction();

  const [loader2, setLoader2] = useState(false);
  const [resumePath, setResumePath] = useState(null);
  const [offerLetterPath, setOfferLetterPath] = useState(null);
  const [editObj, setEditObj] = useState({ resume: false, offerLetter: false });
  const [resumeError, setResumeError] = useState(false);
  const [offerLetterError, setOfferLetterError] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [employee, setEmployee] = useState({});

  const handleGetEmployeeCode = async (e) => {
    const values = {
      employeeId,
    };
    const res = await getEmployeeCodeAPI(values);
    const data = res?.data?.employee;
    setEmployee(data);
  };

  useEffect(() => {
    if (employeeId) {
      handleGetEmployeeCode();
    }
  }, [employeeId]);

  const handleCancel = () => {
    setAccordionOpen(false);
  };

  const getFilenameFromUrl = (url) => {
    if (url !== "" && url !== undefined && url !== null) {
      const parts = url.split("/");
      return parts[parts.length - 1];
    } else {
      return "";
    }
  };

  const handleFileUpload = async (file) => {
    const allowedTypes = ["application/pdf"];

    if (!allowedTypes.includes(file?.type)) {
      toast.error("Only PDF files are allowed.", {
        toastId: "file-type-error",
      });
      return false;
    }

    const maxSize = 1024 * 1024 * 5;
    if (file.size > maxSize) {
      toast.error("File size exceeds the limit (5MB)", {
        toastId: "file-upload-maximum",
      });
      return false;
    }

    try {
      dispatch({ type: "SET_LOADING", payload: true });
      let input = new FormData();
      input.append("type", "employee_documents");
      input.append("employeeId", employeeId);
      input.append("file", file);
      const res = await fileUploadAPI(input);
      dispatch({ type: "SET_LOADING", payload: false });
      if (res?.data?.location) {
        return res?.data?.location;
      }
      return false;
    } catch (err) {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  const handleFileChange = async (event, uploadType) => {
    if (!employeeId) {
      toast.error(
        "Please create an employee account before uploading documents.",
        {
          toastId: "employeeid-error",
        }
      );
      return false;
    }

    const file = event.target.files[0];

    let newName = `${employee?.employeeCode}_${employee?.fullName}`;

    if (uploadType === "RESUME") {
      newName += `_Resume`;
    }
    if (uploadType === "OFFER_LETTER") {
      newName += `_Signed_Offer_Letter`;
    }

    newName = `${newName}.${file?.name?.split(".").pop()}`;
    const renamedFile = new File([file], newName, { type: file.type });

    const location = await handleFileUpload(renamedFile);
    if (location) {
      if (uploadType === "RESUME") {
        setResumePath(location);
        setEditObj({ ...editObj, resume: true });
        if (resumeError) {
          setResumeError(false);
        }
      }
      if (uploadType === "OFFER_LETTER") {
        setOfferLetterPath(location);
        setEditObj({ ...editObj, offerLetter: true });
        if (offerLetterError) {
          setOfferLetterError(false);
        }
      }
    }
  };

  const handleSave = async () => {
    setLoader2(true);

    let isError = false;

    if (!resumePath || !offerLetterPath) {
      setResumeError(true);
      setOfferLetterError(true);
      isError = true;
    } else {
      setResumeError(false);
      setOfferLetterError(false);
    }

    if (isError) {
      setLoader2(false);
      toast.error("Please make changes or edit the file before saving.", {
        toastId: "edit-error",
      });
      return;
    }

    if (!editObj?.resume && !editObj?.offerLetter) {
      setLoader2(false);
      toast.error("Please make changes or edit the file before saving.", {
        toastId: "edit-error",
      });
      return false;
    }

    let body = {
      employeeId: employeeId,
    };
    if (editObj?.resume) {
      body = { ...body, resumeFilePath: resumePath };
    }
    if (editObj?.offerLetter) {
      body = { ...body, offerLetterFilePath: offerLetterPath };
    }
    try {
      const data = await putMyDocumentsAPI(body);
      if (data.status === "Ok") {
        setLoader2(false);
        setEditObj({ resume: false, offerLetter: false });
        return toast.success("Data Saved Successfully", {
          toast: "try-success",
        });
      }
      setLoader2(false);
      return toast.error("Error saving Data", { toast: "Error saving Data" });
    } catch (err) {
      return toast.error(
        "Network error detected please try again after some time",
        { toast: "catch-error" }
      );
    }
  };

  return (
    <div>
      <Loader loading={loader2} />

      <Accordion
        sx={{ mt: 2, boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
        expanded={accordionOpen}
        onChange={() => setAccordionOpen(!accordionOpen)}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon className="Accordianicon" />}
          aria-controls="panel1-content"
          id="panel1-header"
          style={{
            boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
            height: "70px",
          }}
        >
          <Box sx={{ pl: 3 }}>
            {" "}
            <img
              sx={{ pl: 3 }}
              className="editpersonalInfo-logo"
              src={myDocumentsImg}
              alt="info"
            />
          </Box>
          <Box className="editacc-title" sx={{ pl: 3 }}>
            My Documents
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} sx={{ pl: 4, pr: 5, pb: 5, mt: 2 }}>
            <Grid item xs={12} sm={6} md={6} lg={6} sx={{ mt: 2 }}>
              <Box className="label">
                Resume (PDF) <span style={{ color: "red" }}>*</span>
              </Box>
              <TextField
                className="editvalue"
                fullWidth
                placeholder="Browse Files (max of 5mb) "
                value={getFilenameFromUrl(resumePath)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {resumePath && (
                        <IconButton
                          onClick={() => {
                            setResumePath(null);
                          }}
                          edge="end"
                          aria-label="clear file"
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                      <input
                        type="file"
                        accept=".pdf"
                        id="resume-file-upload"
                        style={{ display: "none" }}
                        onClick={handleFileInputReselect}
                        onChange={(e) => {
                          handleFileChange(e, "RESUME");
                        }}
                      />
                      <label htmlFor="resume-file-upload">
                        <IconButton
                          component="span"
                          color="primary"
                          aria-label="attach file"
                        >
                          <AttachFileIcon style={{ color: "#0056B2" }} />
                        </IconButton>
                      </label>
                    </InputAdornment>
                  ),
                }}
              />
              {resumeError && (
                <div className="error-message">Please upload Resume</div>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6} sx={{ mt: 2 }}>
              <Box className="label">
                Signed Offer Letter (PDF){" "}
                <span style={{ color: "red" }}>*</span>
              </Box>
              <TextField
                className="editvalue"
                fullWidth
                placeholder="Browse Files (max of 5mb) "
                value={getFilenameFromUrl(offerLetterPath)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {offerLetterPath && (
                        <IconButton
                          onClick={() => {
                            setOfferLetterPath(null);
                          }}
                          edge="end"
                          aria-label="clear file"
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                      <input
                        type="file"
                        accept=".pdf"
                        id="offer-letter-file-upload"
                        style={{ display: "none" }}
                        onClick={handleFileInputReselect}
                        onChange={(e) => {
                          handleFileChange(e, "OFFER_LETTER");
                        }}
                      />
                      <label htmlFor="offer-letter-file-upload">
                        <IconButton
                          component="span"
                          color="primary"
                          aria-label="attach file"
                        >
                          <AttachFileIcon style={{ color: "#0056B2" }} />
                        </IconButton>
                      </label>
                    </InputAdornment>
                  ),
                }}
              />
              {offerLetterError && (
                <div className="error-message">
                  Please upload Signed Offer Letter
                </div>
              )}
            </Grid>
          </Grid>

          <Grid
            container
            spacing={1}
            sx={{ pl: 4, pr: 5, mt: 5, justifyContent: "center" }}
          >
            <Grid item xs={12} sm={2} md={2} lg={2}>
              <Button className="cancelButton" onClick={handleCancel}>
                Cancel
              </Button>
            </Grid>

            <Grid item xs={12} sm={2} md={2} lg={2}>
              <Button className="saveButton" onClick={handleSave}>
                Save
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default MyDocuments;
